import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Layers,
  Upload,
  Trash2,
  Edit,
  Plus,
  FileImage,
  Ruler
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_CATEGORIES = [
  { value: 'ornament', label: 'Ornament' },
  { value: 'keychain', label: 'Keychain' },
  { value: 'bag_tag', label: 'Bag Tag' },
  { value: 'magnet', label: 'Magnet' },
  { value: 'coaster', label: 'Coaster' },
  { value: 'other', label: 'Other' }
];

const TemplatesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isDragging, setIsDragging] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'ornament',
    width_inches: '',
    height_inches: '',
    file: null
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchTemplates();
  }, [token, navigate]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'image/png') {
        toast.error('Templates must be PNG format to preserve transparency');
        return;
      }
      setFormData({ ...formData, file });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type !== 'image/png') {
        toast.error('Templates must be PNG format to preserve transparency');
        return;
      }
      setFormData({ ...formData, file });
      
      // Auto-fill name from filename if empty
      if (!formData.name) {
        const nameFromFile = file.name.replace(/\.png$/i, '').replace(/[-_]/g, ' ');
        setFormData(prev => ({ ...prev, file, name: nameFromFile }));
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      toast.error('Please select a file');
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', formData.file);
      data.append('name', formData.name);
      data.append('category', formData.category);
      if (formData.width_inches) data.append('width_inches', formData.width_inches);
      if (formData.height_inches) data.append('height_inches', formData.height_inches);

      await axios.post(`${API}/templates/upload`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Template uploaded successfully');
      setUploadDialog(false);
      setFormData({ name: '', category: 'ornament', width_inches: '', height_inches: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    try {
      const data = new FormData();
      data.append('name', editingTemplate.name);
      data.append('category', editingTemplate.category);
      if (editingTemplate.width_inches) data.append('width_inches', editingTemplate.width_inches);
      if (editingTemplate.height_inches) data.append('height_inches', editingTemplate.height_inches);

      await axios.put(`${API}/templates/${editingTemplate.id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Template updated');
      setEditDialog(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const getCategoryLabel = (value) => {
    return TEMPLATE_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Product Templates</h1>
            <p className="text-slate-500 mt-1">Manage product shape templates from your print lab</p>
          </div>
          <Button onClick={() => setUploadDialog(true)} className="btn-action" data-testid="add-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            Upload Template
          </Button>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Label>Filter by category:</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {TEMPLATE_CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline">{filteredTemplates.length} template(s)</Badge>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Layers className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No templates yet</h3>
              <p className="text-slate-500 mt-1">Upload product templates from your print lab</p>
              <Button className="mt-4 btn-action" onClick={() => setUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className="overflow-hidden group" data-testid={`template-${template.id}`}>
                <div className="aspect-square bg-slate-100 relative">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${template.thumbnail_url || template.url}`}
                    alt={template.name}
                    className="w-full h-full object-contain p-2"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={() => {
                        setEditingTemplate(template);
                        setEditDialog(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleDelete(template.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <p className="font-medium text-sm truncate">{template.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline" className="text-xs">
                      {getCategoryLabel(template.category)}
                    </Badge>
                    {template.width_inches && template.height_inches && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Ruler className="h-3 w-3" />
                        {template.width_inches}" x {template.height_inches}"
                      </span>
                    )}
                  </div>
                  {template.width_px && template.height_px && (
                    <p className="text-xs text-slate-400 mt-1">
                      {template.width_px} x {template.height_px}px
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Product Template</DialogTitle>
              <DialogDescription>
                Upload a PNG template from your print lab. The template should have a transparent background where the photo goes.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Metal Ornament - Benelux"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="template-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData({ ...formData, category: val })}
                >
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="width">Width (inches)</Label>
                  <Input
                    id="width"
                    type="number"
                    step="0.001"
                    placeholder="2.25"
                    value={formData.width_inches}
                    onChange={(e) => setFormData({ ...formData, width_inches: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">Height (inches)</Label>
                  <Input
                    id="height"
                    type="number"
                    step="0.001"
                    placeholder="1.5"
                    value={formData.height_inches}
                    onChange={(e) => setFormData({ ...formData, height_inches: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Template File (PNG) *</Label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging 
                      ? 'border-amber-500 bg-amber-50' 
                      : formData.file 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png"
                    onChange={handleFileChange}
                    className="hidden"
                    id="template-file"
                  />
                  <label htmlFor="template-file" className="cursor-pointer block">
                    {formData.file ? (
                      <>
                        <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                          <FileImage className="h-8 w-8" />
                          <span className="font-medium">{formData.file.name}</span>
                        </div>
                        <p className="text-xs text-slate-500">Click or drag to replace</p>
                      </>
                    ) : (
                      <>
                        <Upload className={`h-10 w-10 mx-auto mb-2 ${isDragging ? 'text-amber-500' : 'text-slate-400'}`} />
                        <p className={`text-sm ${isDragging ? 'text-amber-600 font-medium' : 'text-slate-600'}`}>
                          {isDragging ? 'Drop file here!' : 'Drag & drop PNG file here'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">or click to browse</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setUploadDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-action" disabled={uploading} data-testid="upload-template-submit">
                  {uploading ? 'Uploading...' : 'Upload Template'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Template</DialogTitle>
            </DialogHeader>
            {editingTemplate && (
              <form onSubmit={handleEdit} className="space-y-4">
                <div className="flex justify-center mb-4">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${editingTemplate.thumbnail_url || editingTemplate.url}`}
                    alt={editingTemplate.name}
                    className="h-32 object-contain bg-slate-100 rounded-lg p-2"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Template Name</Label>
                  <Input
                    id="edit-name"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={editingTemplate.category} 
                    onValueChange={(val) => setEditingTemplate({ ...editingTemplate, category: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Width (inches)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={editingTemplate.width_inches || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, width_inches: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Height (inches)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={editingTemplate.height_inches || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, height_inches: e.target.value })}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-action">
                    Save Changes
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
  );
};

export default TemplatesPage;
